import type {
  RadioStation,
  RadioStationId,
} from "../config/radio.ts";
import type { RadioStatus } from "../audio/radioSystem.ts";

export interface RadioUiCallbacks {
  onOpenChange(open: boolean): void;
  onSelectStation(stationId: RadioStationId | null): void;
  onSetVolume(volume: number): void;
}

export interface RadioUi {
  update(status: Readonly<RadioStatus>): void;
  toggle(): void;
  close(): void;
  isOpen(): boolean;
  dispose(): void;
}

function element<K extends keyof HTMLElementTagNameMap>(
  tagName: K,
  className: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tagName);
  node.className = className;
  return node;
}

function playbackCopy(status: Readonly<RadioStatus>): string {
  if (status.errorMessage !== null) {
    return status.errorMessage;
  }
  switch (status.playbackState) {
    case "off":
      return "The city is quiet.";
    case "ready":
      return "Ready. Choose the station again to start listening.";
    case "loading":
      return "Tuning the live stream…";
    case "playing":
      return status.muted ? "Playing silently." : "Live from Lithuania.";
    case "blocked":
      return "Press the station again to allow radio audio.";
    case "error":
      return "This station is not answering right now.";
  }
}

export function createRadioUi(
  root: HTMLElement,
  stations: readonly Readonly<RadioStation>[],
  callbacks: Readonly<RadioUiCallbacks>,
): RadioUi {
  const stationById = new Map(stations.map((station) => [station.id, station]));
  let open = false;
  let currentStatus: RadioStatus = {
    stationId: null,
    volume: 0.55,
    muted: false,
    playbackState: "off",
    errorMessage: null,
  };

  const toggleButton = element("button", "radio-toggle");
  toggleButton.type = "button";
  toggleButton.setAttribute("aria-label", "Open Vilnius live radio");
  toggleButton.title = "Radio (N)";
  toggleButton.setAttribute("aria-expanded", "false");
  toggleButton.setAttribute("aria-controls", "radio-panel");
  const toggleSignal = element("span", "radio-toggle-signal");
  toggleSignal.setAttribute("aria-hidden", "true");
  const toggleCopy = element("span", "radio-toggle-copy");
  const toggleKicker = element("span", "radio-toggle-kicker");
  toggleKicker.textContent = "Radio";
  const toggleStation = element("span", "radio-toggle-station");
  toggleCopy.append(toggleKicker, toggleStation);
  const toggleKey = element("kbd", "radio-toggle-key");
  toggleKey.textContent = "N";
  toggleButton.append(toggleSignal, toggleCopy, toggleKey);

  const panel = element("section", "radio-panel");
  panel.id = "radio-panel";
  panel.hidden = true;
  panel.setAttribute("role", "dialog");
  panel.setAttribute("aria-modal", "false");
  panel.setAttribute("aria-labelledby", "radio-title");

  const header = element("header", "radio-header");
  const titleGroup = element("div", "radio-heading-group");
  const eyebrow = element("p", "radio-eyebrow");
  eyebrow.textContent = "Vilnius field receiver";
  const title = element("h2", "radio-title");
  title.id = "radio-title";
  title.textContent = "Live radio";
  titleGroup.append(eyebrow, title);
  const closeButton = element("button", "radio-close");
  closeButton.type = "button";
  closeButton.textContent = "Close";
  closeButton.setAttribute("aria-label", "Close live radio");
  header.append(titleGroup, closeButton);

  const tuner = element("div", "radio-tuner");
  tuner.setAttribute("aria-hidden", "true");
  const tunerTicks = element("span", "radio-tuner-ticks");
  const tunerNeedle = element("span", "radio-tuner-needle");
  tuner.append(tunerTicks, tunerNeedle);

  const stationList = element("div", "radio-stations");
  stationList.setAttribute("role", "group");
  stationList.setAttribute("aria-label", "Lithuanian radio stations");
  const stationButtons = new Map<RadioStationId | null, HTMLButtonElement>();

  const offButton = element("button", "radio-station radio-station-off");
  offButton.type = "button";
  offButton.dataset.stationId = "off";
  offButton.setAttribute("aria-pressed", "true");
  const offMarker = element("span", "radio-station-marker");
  offMarker.setAttribute("aria-hidden", "true");
  const offCopy = element("span", "radio-station-copy");
  const offLabel = element("span", "radio-station-label");
  offLabel.textContent = "None";
  const offDescription = element("span", "radio-station-description");
  offDescription.textContent = "Switch the receiver off";
  offCopy.append(offLabel, offDescription);
  offButton.append(offMarker, offCopy);
  stationList.append(offButton);
  stationButtons.set(null, offButton);

  for (const station of stations) {
    const button = element("button", "radio-station");
    button.type = "button";
    button.dataset.stationId = station.id;
    button.setAttribute("aria-pressed", "false");
    const marker = element("span", "radio-station-marker");
    marker.setAttribute("aria-hidden", "true");
    const copy = element("span", "radio-station-copy");
    const label = element("span", "radio-station-label");
    label.textContent = station.label;
    const description = element("span", "radio-station-description");
    description.textContent = station.description;
    copy.append(label, description);
    button.append(marker, copy);
    stationList.append(button);
    stationButtons.set(station.id, button);
  }

  const footer = element("footer", "radio-footer");
  const statusRow = element("div", "radio-status-row");
  const statusSignal = element("span", "radio-status-signal");
  statusSignal.setAttribute("aria-hidden", "true");
  const statusText = element("p", "radio-status");
  statusText.setAttribute("aria-live", "polite");
  statusRow.append(statusSignal, statusText);

  const volumeRow = element("div", "radio-volume-row");
  const volumeLabel = element("label", "radio-volume-label");
  volumeLabel.htmlFor = "radio-volume";
  volumeLabel.textContent = "Receiver volume";
  const volumeOutput = element("output", "radio-volume-output");
  volumeOutput.htmlFor = "radio-volume";
  const volumeSlider = element("input", "radio-volume");
  volumeSlider.id = "radio-volume";
  volumeSlider.type = "range";
  volumeSlider.min = "0";
  volumeSlider.max = "1";
  volumeSlider.step = "0.01";
  volumeRow.append(volumeLabel, volumeOutput, volumeSlider);

  const sourceLink = element("a", "radio-source-link");
  sourceLink.target = "_blank";
  sourceLink.rel = "noreferrer";
  sourceLink.textContent = "Open station website ↗";
  sourceLink.hidden = true;
  footer.append(statusRow, volumeRow, sourceLink);

  panel.append(header, tuner, stationList, footer);
  root.append(toggleButton, panel);

  const setOpen = (nextOpen: boolean): void => {
    if (open === nextOpen) {
      return;
    }
    open = nextOpen;
    panel.hidden = !open;
    toggleButton.hidden = open;
    toggleButton.dataset.open = String(open);
    toggleButton.setAttribute("aria-expanded", String(open));
    callbacks.onOpenChange(open);
    if (open) {
      closeButton.focus({ preventScroll: true });
    }
  };

  const render = (): void => {
    const station = currentStatus.stationId === null
      ? null
      : stationById.get(currentStatus.stationId) ?? null;
    const stationLabel = station?.label ?? "Off";
    toggleStation.textContent = stationLabel;
    toggleSignal.dataset.state = currentStatus.playbackState;
    panel.dataset.playbackState = currentStatus.playbackState;
    statusSignal.dataset.state = currentStatus.playbackState;
    statusText.textContent = playbackCopy(currentStatus);
    volumeSlider.value = String(currentStatus.volume);
    volumeOutput.value = `${Math.round(currentStatus.volume * 100)}%`;
    sourceLink.hidden = station === null;
    if (station !== null) {
      sourceLink.href = station.websiteUrl;
      sourceLink.setAttribute("aria-label", `Open ${station.label} website`);
    } else {
      sourceLink.removeAttribute("href");
      sourceLink.removeAttribute("aria-label");
    }
    for (const [stationId, button] of stationButtons) {
      const active = currentStatus.stationId === stationId;
      button.dataset.active = String(active);
      button.setAttribute("aria-pressed", String(active));
    }
  };

  const onToggle = (): void => setOpen(!open);
  const onClose = (): void => setOpen(false);
  const onStationClick = (event: Event): void => {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }
    const button = target.closest<HTMLButtonElement>(".radio-station");
    if (button === null) {
      return;
    }
    const rawStationId = button.dataset.stationId;
    callbacks.onSelectStation(
      rawStationId === "off" ? null : rawStationId as RadioStationId,
    );
  };
  const onVolume = (): void => {
    callbacks.onSetVolume(Number(volumeSlider.value));
  };
  const onKeyDown = (event: KeyboardEvent): void => {
    if (open && event.code === "Escape") {
      event.preventDefault();
      setOpen(false);
      toggleButton.focus({ preventScroll: true });
    }
  };

  toggleButton.addEventListener("click", onToggle);
  closeButton.addEventListener("click", onClose);
  stationList.addEventListener("click", onStationClick);
  volumeSlider.addEventListener("input", onVolume);
  window.addEventListener("keydown", onKeyDown);
  render();

  return {
    update(status): void {
      currentStatus = { ...status };
      render();
    },
    toggle(): void {
      setOpen(!open);
    },
    close(): void {
      setOpen(false);
    },
    isOpen(): boolean {
      return open;
    },
    dispose(): void {
      toggleButton.removeEventListener("click", onToggle);
      closeButton.removeEventListener("click", onClose);
      stationList.removeEventListener("click", onStationClick);
      volumeSlider.removeEventListener("input", onVolume);
      window.removeEventListener("keydown", onKeyDown);
      toggleButton.remove();
      panel.remove();
    },
  };
}
